import { useCallback } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import {
	applyDisconnectedEcallsToCache,
	removeErefFromCache,
	spliceFurnitureIntoCache,
} from "helpers/quotationCache.helper"
import { enqueueMutation } from "store/mutationQueue.store"
import AnnotationService from "services/annotations.service"
import { erefQueueKey } from "../ReferenceSidebar/erefQueueKey"
import type { useDeleteEref } from "hooks/api/quotation/quotations.hook"
import type { TErefDeleteResponse, TErefWithFurniture } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"

// Eref delete — REQ-376 (toolbar + context menu + Delete/Backspace key) and REQ-295 (the eref
// sidebar's ⋯ menu), all four raising the same confirmation. Deleting an eref is NOT a
// cascade: the backend detaches the connected furniture and ecall elevations and returns them in
// their post-delete state (full payloads, bumped versions), so the cache has to apply those
// updates alongside removing the eref itself — otherwise the next edit of a detached object would
// send a stale version and fail the optimistic-lock check.
//
// The request goes through the eref's own mutation queue (same key as the sidebar / coordinate
// commits) so a delete can never overtake an in-flight status or comment PATCH on that eref. The
// endpoint itself takes no `version` — delete is not version-checked — so nothing is chained out.
export type ErefDeleteContext = {
	numericQuotationIdRef: MutableRefObject<number | undefined>
	setSelectionToolbar: (value: null) => void
	setDeleteConfirm: (value: null) => void
	setBatchPending: (value: boolean) => void
	deleteErefAsync: ReturnType<typeof useDeleteEref>["mutateAsync"]
	/** Post-delete cleanup owned by the orchestrator: close the eref sidebar / jump session. */
	onErefDeleted: (erefId: number) => void
	queryClient: QueryClient
	success: (text: string) => void
	formatError: (error: unknown) => void
}

export const useErefDelete = (ctx: ErefDeleteContext) => {
	const {
		numericQuotationIdRef,
		setSelectionToolbar,
		setDeleteConfirm,
		setBatchPending,
		deleteErefAsync,
		onErefDeleted,
		queryClient,
		success,
		formatError,
	} = ctx

	const runErefDelete = useCallback(
		async (eref: TErefWithFurniture) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			setBatchPending(true)
			// Failures are handled INSIDE the queued task (as the eref sidebar's mutations do), so the
			// queue's promise chain never sees a rejection.
			await enqueueMutation(erefQueueKey(eref.id), eref.version, async () => {
				try {
					const resp = (await deleteErefAsync({ quotationId: qid, erefId: eref.id }))?.data as
						| TErefDeleteResponse
						| undefined
					const updatedFurniture = resp?.updated_furniture_list ?? []
					const updatedEcalls = resp?.updated_ecalls ?? []

					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
						let next = removeErefFromCache(cached, eref.id)
						// Detached furniture come back whole (eref_id: null + new version) — splice each in.
						for (const furniture of updatedFurniture) next = spliceFurnitureIntoCache(next, furniture)
						// Same for the ecalls that lost an elevation link; the shared helper merges them
						// version-guarded (it ignores an entry the cache already holds at a newer version).
						return applyDisconnectedEcallsToCache(next, updatedEcalls, undefined)
					})

					AnnotationService.removeErefAnnotation(eref.id)
					// The detached furniture keep their highlights and colors; only the ecalls need a
					// repaint, since their color is derived from the eref links they just lost.
					for (const ecall of updatedEcalls) AnnotationService.syncEcallConnectivity(ecall)

					onErefDeleted(eref.id)
					AnnotationService.deselectAll()
					setSelectionToolbar(null)
					setDeleteConfirm(null)
					success("Elevation reference deleted")
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
				} finally {
					setBatchPending(false)
				}
			})
		},
		[
			numericQuotationIdRef,
			setSelectionToolbar,
			setDeleteConfirm,
			setBatchPending,
			deleteErefAsync,
			onErefDeleted,
			queryClient,
			success,
			formatError,
		],
	)

	return { runErefDelete }
}
